<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 id="renal-non-cystic-test-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="renal-non-cystic-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col">
              <p>
                This Medicare funded
                <a href="https://www.vcgs.org.au/node/1436" target="_blank"
                  >Heritable Kidney Disease</a
                >
                test analyses genes associated with heritable kidney disease
                (other than cystic disease or Alport syndrome) via Exome
                sequencing. Medicare item number: 73402
              </p>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col class="custom-col">
              <v-alert text color="info">
                <table>
                  <tr>
                    <td>
                      <b>Eligibility criteria:</b>
                    </td>
                  </tr>
                  <tr>
                    Requests must be made by a consultant clinical geneticist or
                    specialist nephrologist for a Medicare eligible patient with
                    chronic kidney disease (other than cystic disease or Alport
                    syndrome) who is strongly suspected of having a monogenic
                    condition.
                  </tr>
                  <br />
                  <tr>
                    If your patient does not meet these criteria, you can still
                    order and pay by selecting Exome Focused and with the
                    Kidneyome gene panel.
                  </tr>
                </table>
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical features</span>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  persistent-hint
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                  ><template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert/>
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                rows="1"
                auto-grow
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                rows="2"
                auto-grow
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>
        </v-container>
        <RenalNonCysticConsent
          ref="renalNonCysticConsent"
          :is-trio="false"
          :force-form-dirty="forceFormDirty"
          :testName="testName"
          :testId="testId"
        ></RenalNonCysticConsent>

        <TestPriceContainer
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import PhenotypesMixin from "../../mixins/PhenotypesMixin";
import GenesMixin from "../../mixins/GenesMixin.vue";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import RenalNonCysticConsent from "./RenalNonCysticConsentContainer";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceContainer from "../common/TestPriceContainer";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import { EventBus } from "@/event-bus";
import BrokenApiAlert from "../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../common/BadSearchQueryAlert.vue";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.renalNonCysticTestPricing = await this.execRetrieveJson(
      "json/pricing/renal_non_cystic_pricing.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");

    const element = document.getElementById("renal-non-cystic-test-title");

    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    this.urgency = "Standard";
    this.familyType = "Singleton";
    this.analysisType = "Exome Focused";

    const pricing = this.renalNonCysticTestPricing[this.urgency][
      this.familyType
    ][this.analysisType];

    this.price = pricing.price;
    this.testCode = pricing.testCode;
    this.medicare = pricing.medicare;

    this.$emit("toggleTotalPrice", this.price, this.testId);
    EventBus.$emit("medicareRenalNonCysticStatus", true);
  },
  mixins: [FormRulesMixin, PhenotypesMixin, RetrieveJSONMixin, GenesMixin],
  name: "",
  components: { RenalNonCysticConsent, TestPriceContainer, BrokenApiAlert, BadSearchQueryAlert },
  data: () => ({
    valid: false,
    urgent: false,
    urgentReason: "",
    renalNonCysticTestPricing: null,
    cgApproved: true,
    cgDetails: "",
    genePanels: ["Kidneyome_SuperPanel"],
    phenotypes: [],
    familyHx: "",
    clinicalNotes: "",
    ageOfOnset: "",
    urgency: null,
    familyType: null,
    analysisType: null,
    indGenes: [],
    billTo: "MEDICARE",
    searchInputGenes: null,
    searchInputPhenotypes: null,
    price: null,
    testCode: null,
    medicare: true,
  }),
  methods: {
    isFormValid: function() {
      if (typeof this.$refs.renalNonCysticConsent !== "undefined") {
        return this.$refs.renalNonCysticConsent.isFormValid();
      } else return false;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];

      for (let [familyKey, familyComponent] of Object.entries(this.$refs)) {
        if (familyComponent && familyKey.substring(0, 12) === "familyMember") {
          familyDetails.push(familyComponent.transferData());
        }
      }

      const finalGenePanels =
        this.genePanels[0] === "Kidneyome_SuperPanel"
          ? "275 - Kidneyome_SuperPanel"
          : "";

      let result = {
        urgent: this.urgent,
        urgency: this.urgency,
        urgentReason: "",
        testType: this.testId,
        familyType: this.familyType,
        analysisType: this.analysisType,
        rapid: this.rapidTest,
        genePanels: finalGenePanels,
        indGenes: this.getFinalIndGenes(this.indGenes),
        phenotypes: this.getFinalPhenotypes(this.phenotypes),
        ageOfOnset: this.ageOfOnset,
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
        cgApproved: this.cgApproved,
        cgDetails: this.cgDetails,
        billTo: this.billTo,
      };

      // Consent data is present, except if the test is medicare but not approved by gc
      if (this.$refs.renalNonCysticConsent) {
        const consentData = this.$refs.renalNonCysticConsent.transferData();

        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>
